import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Divider, FloatButton, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, Typography, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loadCompaniesInitial, loadCompaniesNext } from '../../store/adminSlice';
import UserCompanyMembersModal from './UserCompanyMembersModal';
import { db } from '../../config/firebase-config';
import AddCompanyModal from '../../components/Company/AddCompanyModal';
import { CompanyModel } from '../../types/admin';

const { Text } = Typography;
const MODULE_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  customContent: 'Custom Content',
  generation: 'Generation',
  brandResearch: 'Brand Research',
};

const MODULE_COLORS: Record<string, string> = {
  shopify: 'blue',
  customContent: 'green',
  brandResearch: 'gold',
};

const getQuotaMax = (company: CompanyModel | null | undefined, key: 'shopify' | 'customContent'): number | null => {
  const value = company?.generationQuota?.[key]?.max;
  return typeof value === 'number' ? value : null;
};

const getQuotaUsage = (company: CompanyModel | null | undefined, key: 'shopify' | 'customContent'): number =>
  Number(company?.generationUsage?.[key] ?? 0);

const getQuotaMode = (company: CompanyModel | null | undefined, key: 'shopify' | 'customContent') =>
  getQuotaMax(company, key) === null ? 'unlimited' : 'limited';

const formatQuotaLabel = (company: CompanyModel | null | undefined, key: 'shopify' | 'customContent') => {
  const max = getQuotaMax(company, key);
  const used = getQuotaUsage(company, key);
  return max === null ? `Unlimited, used ${used}` : `${used} / ${max}`;
};

const AdminCompaniesTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const companies = useAppSelector(s => s.admin.companies.items);
  const cCursor = useAppSelector(s => s.admin.companies.cursor);
  const cLoading = useAppSelector(s => s.admin.companies.loading);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (companies.length === 0 && !cLoading) {
      dispatch(loadCompaniesInitial(50));
    }
  }, [companies.length, cLoading, dispatch]);

  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleScroll = useCallback(({ target }: any) => {
    if (cLoading || !cCursor) return;
    const { scrollTop, scrollHeight, clientHeight } = target as HTMLElement;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      dispatch(loadCompaniesNext({ pageSize: 50, cursor: cCursor }));
    }
  }, [cCursor, cLoading, dispatch]);

  const debounceScroll = useMemo(() => debounce(handleScroll, 200), [handleScroll]);

  const formatDate = useCallback((value: any) => {
    if (!value) return '-';
    const d = typeof value?.toDate === 'function' ? value.toDate() : (value instanceof Date ? value : null);
    return d ? d.toLocaleDateString() : String(value);
  }, []);

  const getDateValue = useCallback((value: any) => {
    if (!value) return 0;
    const d = typeof value?.toDate === 'function' ? value.toDate() : (value instanceof Date ? value : null);
    return d ? d.getTime() : 0;
  }, []);

  const getUserCount = useCallback((company: any) => Object.keys(company?.accessLevels || {}).length, []);

  const statusFilters = useMemo(() => ([
    { text: 'created', value: 'created' },
    { text: 'hidden', value: 'hidden' },
    { text: 'delete', value: 'delete' },
  ]), []);

  const userCountFilters = useMemo(() => ([
    { text: '0', value: '0' },
    { text: '1-2', value: '1-2' },
    { text: '3-5', value: '3-5' },
    { text: '6-10', value: '6-10' },
    { text: '10+', value: '10+' },
  ]), []);

  const moduleFilters = Object.entries(MODULE_LABELS).map(([value, text]) => ({
    text,
    value,
  }));

  const columns = useMemo(() => ([
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: statusFilters,
      onFilter: (value, record) => String(record?.status) === String(value),
    },
    {
      title: 'Modules',
      filters: moduleFilters,
      onFilter: (value, record) => Boolean(record?.modules?.[String(value)]),
      render: (_: any, r: any) => {
        const modules = r?.modules || {};
        const entries = Object.entries(modules).filter(([, enabled]) => Boolean(enabled));
        if (entries.length === 0) return '-';
        return (
          <>
            {entries.map(([key]) => (
              <Tag key={key} color={MODULE_COLORS[key] ?? 'default'}>
                {MODULE_LABELS[key] ?? key}
              </Tag>
            ))}
          </>
        );
      },
    },
    {
      title: 'Quotas',
      render: (_: any, r: CompanyModel) => (
        <Space direction="vertical" size={0}>
          <span>Shopify: {formatQuotaLabel(r, 'shopify')}</span>
          <span>Custom: {formatQuotaLabel(r, 'customContent')}</span>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (v: any) => formatDate(v),
      sorter: (a: any, b: any) => getDateValue(a.createdAt) - getDateValue(b.createdAt),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      render: (v: any) => formatDate(v),
      sorter: (a: any, b: any) => getDateValue(a.updatedAt) - getDateValue(b.updatedAt),
    },
    {
      title: 'Users',
      filters: userCountFilters,
      onFilter: (value, record) => {
        const count = getUserCount(record);
        switch (value) {
          case '0':
            return count === 0;
          case '1-2':
            return count >= 1 && count <= 2;
          case '3-5':
            return count >= 3 && count <= 5;
          case '6-10':
            return count >= 6 && count <= 10;
          case '10+':
            return count >= 10;
          default:
            return true;
        }
      },
      sorter: (a: any, b: any) => getUserCount(a) - getUserCount(b),
      render: (_: any, r: any) => {
        const count = getUserCount(r);
        return (
          <Button type="link" onClick={() => { setSelectedCompany(r); setIsUsersOpen(true); }}>
            {count}
          </Button>
        );
      },
    },
    {
      title: 'Actions',
      render: (_: any, r: any) => (
        <Button
          icon={<SettingOutlined />}
          onClick={() => {
            setEditingCompany(r);
            setIsEditOpen(true);
            form.setFieldsValue({
              name: r?.name ?? '',
              modules: {
                shopify: Boolean(r?.modules?.shopify),
                customContent: Boolean(r?.modules?.customContent),
                generation: Boolean(r?.modules?.generation),
                brandResearch: Boolean(r?.modules?.brandResearch),
              },
              generationQuotaMode: {
                shopify: getQuotaMode(r, 'shopify'),
                customContent: getQuotaMode(r, 'customContent'),
              },
              generationQuota: {
                shopifyMax: getQuotaMax(r, 'shopify') ?? undefined,
                customContentMax: getQuotaMax(r, 'customContent') ?? undefined,
              },
            });
          }}
        />
      ),
    },
  ]) as ColumnsType<any>, [
    formatDate,
    getDateValue,
    getUserCount,
    moduleFilters,
    statusFilters,
    userCountFilters,
    form,
  ]);

  return (
    <>
      <div style={{ position: 'relative' }}>
        <Table
          size="small"
          bordered
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={companies}
          pagination={false}
          loading={cLoading && companies.length === 0}
          scroll={{ y: 570 }}
          onScroll={debounceScroll}
        />

        <Tooltip title="Create company">
          <FloatButton
            icon={<PlusOutlined />}
            onClick={() => setIsAddOpen(true)}
            type="primary"
            style={{ right: 24, bottom: 100 }}
          />
        </Tooltip>
      </div>

      <AddCompanyModal
        open={isAddOpen}
        handleClose={() => {
          setIsAddOpen(false);
          dispatch(loadCompaniesInitial(50));
        }}
      />
      <UserCompanyMembersModal
        open={isUsersOpen}
        onCancel={() => setIsUsersOpen(false)}
        company={selectedCompany}
        onChanged={() => dispatch(loadCompaniesInitial(50))}
      />
      <Modal
        title="Company Settings"
        open={isEditOpen}
        onCancel={() => setIsEditOpen(false)}
        okText="Save"
        okButtonProps={{ loading: saving }}
        onOk={async () => {
          if (!editingCompany) return;
          try {
            const values = await form.validateFields();
            const shopifyQuotaMax = values.generationQuotaMode?.shopify === 'limited'
              ? Number(values.generationQuota?.shopifyMax)
              : null;
            const customContentQuotaMax = values.generationQuotaMode?.customContent === 'limited'
              ? Number(values.generationQuota?.customContentMax)
              : null;

            if (values.generationQuotaMode?.shopify === 'limited' && Number.isNaN(shopifyQuotaMax)) {
              throw new Error('Please provide a Shopify generation limit.');
            }
            if (values.generationQuotaMode?.customContent === 'limited' && Number.isNaN(customContentQuotaMax)) {
              throw new Error('Please provide a Custom Content generation limit.');
            }

            setSaving(true);
            await updateDoc(doc(db, 'companies', editingCompany.id), {
              name: values.name,
              modules: {
                shopify: Boolean(values.modules?.shopify),
                customContent: Boolean(values.modules?.customContent),
                generation: Boolean(values.modules?.generation),
                brandResearch: Boolean(values.modules?.brandResearch),
              },
              generationQuota: {
                shopify: { max: shopifyQuotaMax },
                customContent: { max: customContentQuotaMax },
                brandResearch: editingCompany.generationQuota?.brandResearch ?? { max: null },
                brandKeywordAssignment: editingCompany.generationQuota?.brandKeywordAssignment ?? { max: null },
              },
              updatedAt: Timestamp.now(),
            });
            notification.success({
              message: 'Company updated',
              description: 'Settings were saved successfully.',
            });
            setIsEditOpen(false);
            dispatch(loadCompaniesInitial(50));
          } catch (error) {
            if (error instanceof Error) {
              notification.error({ message: 'Failed to update company', description: error.message });
            }
          } finally {
            setSaving(false);
          }
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Popconfirm
              title="Mark company as deleted?"
              description="This will set status to deleted."
              onConfirm={async () => {
                if (!editingCompany) return;
                try {
                  setSaving(true);
                  await updateDoc(doc(db, 'companies', editingCompany.id), {
                    status: 'delete',
                    updatedAt: Timestamp.now(),
                  });
                  notification.success({
                    message: 'Company marked as deleted',
                    description: 'Status was updated to deleted.',
                  });
                  setIsEditOpen(false);
                  dispatch(loadCompaniesInitial(50));
                } catch (error) {
                  if (error instanceof Error) {
                    notification.error({ message: 'Failed to update status', description: error.message });
                  }
                } finally {
                  setSaving(false);
                }
              }}
              okButtonProps={{ danger: true }}
            >
              <Button danger disabled={saving || editingCompany?.status === 'delete'}>
                Mark Deleted
              </Button>
            </Popconfirm>
            <CancelBtn />
            <OkBtn />
          </>
        )}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a company name.' }]}
          >
            <Input placeholder="Company name" />
          </Form.Item>
          <Form.Item label="Modules">
            <Space size="large" wrap>
              <Form.Item name={['modules', 'shopify']} valuePropName="checked" noStyle>
                <Switch checkedChildren="Shopify" unCheckedChildren="Shopify" />
              </Form.Item>
              <Form.Item name={['modules', 'customContent']} valuePropName="checked" noStyle>
                <Switch checkedChildren="Custom Content" unCheckedChildren="Custom Content" />
              </Form.Item>
              <Form.Item name={['modules', 'generation']} valuePropName="checked" noStyle>
                <Switch checkedChildren="Generation" unCheckedChildren="Generation" />
              </Form.Item>
              <Form.Item name={['modules', 'brandResearch']} valuePropName="checked" noStyle>
                <Switch checkedChildren="Brand Research" unCheckedChildren="Brand Research" />
              </Form.Item>
            </Space>
          </Form.Item>

          <Divider orientation="left">Generation Limits</Divider>

          <Form.Item label="Shopify Limit" required>
            <Space align="start" wrap>
              <Form.Item name={['generationQuotaMode', 'shopify']} noStyle initialValue="unlimited">
                <Select
                  style={{ width: 140 }}
                  options={[
                    { value: 'unlimited', label: 'Unlimited' },
                    { value: 'limited', label: 'Limited' },
                  ]}
                />
              </Form.Item>
              <Form.Item shouldUpdate={(prev, cur) => prev?.generationQuotaMode?.shopify !== cur?.generationQuotaMode?.shopify} noStyle>
                {({ getFieldValue }) => (
                  getFieldValue(['generationQuotaMode', 'shopify']) === 'limited' ? (
                    <Form.Item name={['generationQuota', 'shopifyMax']} noStyle>
                      <InputNumber min={0} precision={0} placeholder="Max generations" />
                    </Form.Item>
                  ) : (
                    <Text type="secondary">No cap</Text>
                  )
                )}
              </Form.Item>
            </Space>
            <div>
              <Text type="secondary">Used so far: {getQuotaUsage(editingCompany, 'shopify')}</Text>
            </div>
          </Form.Item>

          <Form.Item label="Custom Content Limit" required>
            <Space align="start" wrap>
              <Form.Item name={['generationQuotaMode', 'customContent']} noStyle initialValue="unlimited">
                <Select
                  style={{ width: 140 }}
                  options={[
                    { value: 'unlimited', label: 'Unlimited' },
                    { value: 'limited', label: 'Limited' },
                  ]}
                />
              </Form.Item>
              <Form.Item shouldUpdate={(prev, cur) => prev?.generationQuotaMode?.customContent !== cur?.generationQuotaMode?.customContent} noStyle>
                {({ getFieldValue }) => (
                  getFieldValue(['generationQuotaMode', 'customContent']) === 'limited' ? (
                    <Form.Item name={['generationQuota', 'customContentMax']} noStyle>
                      <InputNumber min={0} precision={0} placeholder="Max generations" />
                    </Form.Item>
                  ) : (
                    <Text type="secondary">No cap</Text>
                  )
                )}
              </Form.Item>
            </Space>
            <div>
              <Text type="secondary">Used so far: {getQuotaUsage(editingCompany, 'customContent')}</Text>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminCompaniesTab;
